import React, { useState, useEffect, useRef  } from "react";
import { useRecoilState } from "recoil";
import { chatMessagesAtom, sessionIDAtom, userIDAtom } from "../../store/atoms";
import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
} from "aws-amplify/auth";
import LoginPage from "../login/LoginPage";
import Moments from "../moments/Moments";
import ServicesSidebar from "../services/ServicesSidebar";
import Packages from "../packages/Packages";
import Memory from "../memory/Memory";
import Wishlist from "../wishlist/Wishlist";
import Chat from "../mindyAI/Chat";
import CrisisChat from "../mindyAI/CrisisChat";
import ServiceSelector from "../ServiceSelector";
import MemoryPage from "./MemoryPage";
import { useIsMobile } from '../../customHook/useIsMobile';
import { DEMO_AUTH_MODE, DEMO_SESSION_KEY, readDemoSession } from '../../config/demoAuth';

const DashboardPage = () => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [currentPage, setCurrentPage] = useState("login");
  const [user, setUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState({});
  const [pageTransition, setPageTransition] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("Ask Mindy");
  console.log("Active Menu:", activeMenu);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [aiModal, setAiModal] = useState(false);
  const [showServiceSelector, setShowServiceSelector] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cognitoName, setCognitoName] = useState("");
  const [chatMessages, setChatMessages] = useRecoilState(chatMessagesAtom);
  const [sessionID, setSessionID] = useRecoilState(sessionIDAtom);

  const mobileWidth = useIsMobile(); 

  useEffect(() => {
    checkAuthState();
    if (DEMO_AUTH_MODE) {
      return;
    }
    const getUser = async () => {
      try {
        const attributes = await fetchUserAttributes();
        if (attributes && attributes.name) {
          setCognitoName(attributes.name);
        }
      } catch (error) {
        console.error("Error fetching user attributes:", error);
      }
    };
    getUser();
  }, []);

  const wishlistRef = useRef(null);
  const momentsRef = useRef(null);
  const aboutMeRef = useRef(null);

 
const [isMuted, setIsMuted] = useState(false);
const videoRef = useRef(null);
const [showFullscreenVideo, setShowFullscreenVideo] = useState(false);


const toggleMute = () => {
  setIsMuted(prev => !prev);
};

  
  // Click outside handler for all side pages
  useEffect(() => {
    const handleClickOutside = (event) => {
      // For Wishes page
      if (activeMenu === "Wishes" && 
          wishlistRef.current && 
          !wishlistRef.current.contains(event.target)) {
        setActiveMenu("Ask Mindy");
        setWishlistOpen(false);
      }
      
      // For Moments page
      if (activeMenu === "Moments" && 
          momentsRef.current && 
          !momentsRef.current.contains(event.target)) {
        setActiveMenu("Ask Mindy");
        // Add if you have momentsOpen state
        // setMomentsOpen(false);
      }
      
      // For About Me page
      if (activeMenu === "About Me" && 
          aboutMeRef.current && 
          !aboutMeRef.current.contains(event.target)) {
        setActiveMenu("Ask Mindy");
        setMemoryOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMenu]);



  const navigateToPage = (page) => {
    setPageTransition(true);
    setTimeout(() => {
      setCurrentPage(page);
      setPageTransition(false);
    }, 300);
  };

  const checkAuthState = async () => {
    if (DEMO_AUTH_MODE) {
      const session = readDemoSession();
      if (session) {
        setUser(session);
        setUserAttributes(session.attributes || {});
        setCurrentPage("dashboard");
      } else {
        setUser(null);
        setUserAttributes({});
        setCurrentPage("login");
      }
      setLoading(false);
      return;
    }

    try {
      const authenticatedUser = await getCurrentUser();
      console.log('[MindyKnows] User authenticated:', authenticatedUser.username);
      const attributes = await fetchUserAttributes();
      console.log("🔍 Cognito Attributes:", attributes);

      setUser(authenticatedUser);
      setUserAttributes(attributes);
      setCurrentPage("dashboard");
    } catch (error) {
      console.log("[MindyKnows] No authenticated user found");
      setUser(null);
      setCurrentPage("login");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      console.log("🚪 [MindyKnows] Signing out user...");
      setSessionID("");
      setChatMessages([]);
      if (DEMO_AUTH_MODE) {
        localStorage.removeItem(DEMO_SESSION_KEY);
      } else {
        await signOut();
      }
      console.log(" [MindyKnows] Sign out successful");
      setUser(null);

      navigateToPage("login");
    } catch (error) {
      console.error("❌ [MindyKnows] Sign out failed:", error);
      setUser(null);
      navigateToPage("login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  if (currentPage === "login") {
    return <LoginPage navigateToPage={navigateToPage} setUser={setUser} />;
  }

  const handleMenuClick = (label) => {
  setActiveMenu(label);

  // reset all
  setServicesOpen(false);
  setWishlistOpen(false);
  setMemoryOpen(false);

  // Clear chat messages when switching to chat-enabled tabs
  if (label === "Ask Mindy") {
    const newSessionID =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    setSessionID(newSessionID);
    setChatMessages([]);
    
    // Get user attributes to determine welcome message
    const getUserAttributes = async () => {
      try {
        if (DEMO_AUTH_MODE) {
          const attributes = userAttributes || {};
          const givenName = attributes['custom:given_name'] || attributes.given_name || 'Friend';
          const isParentFlag =
            attributes['custom:parentProfile'] &&
            String(attributes['custom:parentProfile']).toLowerCase() === 'true';

          const parentWelcomeMessage = {
            text: {
              reply: `Hey <strong>${givenName}</strong>, hope you're having a banner day! 😊 How can I help?`,
              contains_html: true,
            },
            type: 'mindy',
          };
          const studentWelcomeMessage = {
            text: {
              reply: `Hey <strong>${givenName}</strong>, nice to see you! 😊 What can I help with today?`,
              contains_html: true,
            },
            type: 'mindy',
          };
          setChatMessages([isParentFlag ? parentWelcomeMessage : studentWelcomeMessage]);
          return;
        }

        const attributes = await fetchUserAttributes();
        
        if (attributes) {
          // Extract names from Cognito attributes
          const givenName =
            attributes['custom:given_name'] ||
            attributes.given_name ||
            attributes.name ||
            'Friend';

          const isParentFlag =
            attributes['custom:parentProfile'] &&
            attributes['custom:parentProfile'].toLowerCase() === 'true';

          // Define welcome messages (same as in Chat component)
          const parentWelcomeMessage = {
            text: {
              reply: `Hey <strong>${givenName}</strong>, hope you're having a banner day! 😊 How can I help?`,
              contains_html: true,
            },
            type: 'mindy',
          };

          const studentWelcomeMessage = {
            text: {
              reply: `Hi <strong>${givenName}</strong>. Nelson Mandela said, "It always seems impossible until it's done." So what do you want to make possible today? 🚀`,
              contains_html: true,
            },
            type: 'mindy',
          };

          // Set the appropriate welcome message
          setChatMessages([isParentFlag ? parentWelcomeMessage : studentWelcomeMessage]);
        }
      } catch (error) {
        console.error('Error fetching user attributes:', error);
        // Fallback to default message if there's an error
        setChatMessages([
          // {
          //   text: {
          //     reply: "Hey! 😊 How's it going? I hope you're doing great today. Let me know how can I help you! 💕",
          //   },
          //   type: 'mindy',
          // },
        ]);
      }
    };

    getUserAttributes();
  }

  if (label === "Get a Hand") setServicesOpen(true);
  if (label === "Get a Package") setServicesOpen(true);
  if (label === "Wishes") setWishlistOpen(true);
  if (label === "About Me") setMemoryOpen(true);
};

   const userName =
    userAttributes?.given_name ||
    userAttributes?.family_name ||
    userAttributes?.['custom:given_name'] ||
    userAttributes?.['custom:family_name'] ||
    user?.username ||
    "User";

  const isParent =
    userAttributes?.["custom:parentProfile"] &&
    userAttributes["custom:parentProfile"].toLowerCase() === "true";

  const accountType = isParent ? "Parent Account" : "Student Account";

  const handleGetHelpNow = () => {
    // Switch to Chat page
    setActiveMenu("Ask Mindy");
    setShowServiceSelector(false);
    setChatMessages([]);

    // Add a short delay so the Chat component mounts before sending message
    setTimeout(() => {
      setChatMessages([
        {
          sender: "user",
          text: "Talk to Mindy Now",
          timestamp: new Date().toISOString(),
        },
      ]);
      // Trigger mock API or real backend response
      setChatMessages((prev) => [
        ...prev,
        {
          sender: "assistant",
          text: "Hi there! I’m Mindy 👋 How can I help you today?",
          timestamp: new Date().toISOString(),
        },
      ]);
    }, 400);
  };

  // Menu Items
  const menuItems = [
  {
    label: 'Ask Mindy',
    svg: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          opacity="0.5"
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88836 21.6244 10.4003 22 12 22Z"
          fill="white"
        />
        <path
          d="M7.825 12.85C7.36937 12.85 7 13.2194 7 13.675C7 14.1306 7.36937 14.5 7.825 14.5H13.875C14.3306 14.5 14.7 14.1306 14.7 13.675C14.7 13.2194 14.3306 12.85 13.875 12.85H7.825Z"
          fill="white"
        />
        <path
          d="M7.825 9C7.36937 9 7 9.36937 7 9.825C7 10.2806 7.36937 10.65 7.825 10.65H16.625C17.0806 10.65 17.45 10.2806 17.45 9.825C17.45 9.36937 17.0806 9 16.625 9H7.825Z"
          fill="white"
        />
      </svg>
    ),
  },
  { label: 'Get a Hand', 
  svg: (
    <svg 
      xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M7.01988 17.1824C8.4829 17.9501 9.21441 18.334 10.0013 18.334V10.0007L2.19965 5.89453C2.18816 5.91299 2.17688 5.93163 2.1658 5.95045C1.66797 6.79587 1.66797 7.84789 1.66797 9.95192V10.0494C1.66797 12.1534 1.66797 13.2055 2.1658 14.0509C2.66363 14.8963 3.56016 15.3668 5.35321 16.3077L7.01988 17.1824Z" fill="white"/>
      <path opacity="0.7" d="M14.649 3.69423L12.9823 2.81961C11.5193 2.05185 10.7878 1.66797 10.0009 1.66797C9.21398 1.66797 8.48247 2.05185 7.01945 2.81961L5.35278 3.69423C3.59965 4.61424 2.70356 5.08448 2.19922 5.89517L10.0009 10.0013L17.8025 5.89517C17.2982 5.08448 16.4021 4.61424 14.649 3.69423Z" fill="white"/>
      <path opacity="0.5" d="M17.8355 5.95045C17.8244 5.93163 17.8131 5.91299 17.8017 5.89453L10 10.0007V18.334C10.7869 18.334 11.5184 17.9501 12.9814 17.1824L14.6481 16.3077C16.4411 15.3668 17.3377 14.8963 17.8355 14.0509C18.3333 13.2055 18.3333 12.1534 18.3333 10.0494V9.95192C18.3333 7.84789 18.3333 6.79587 17.8355 5.95045Z" fill="white"/>
      <path d="M5.26782 3.73562C5.29518 3.72126 5.32276 3.70679 5.35055 3.6922L6.59515 3.03906L14.1791 7.21025L17.5322 5.53371C17.647 5.66364 17.7467 5.80057 17.8341 5.94906C17.9588 6.16074 18.0522 6.38537 18.1223 6.63623L14.7903 8.30221V10.8326C14.7903 11.1778 14.5105 11.4576 14.1653 11.4576C13.8201 11.4576 13.5403 11.1778 13.5403 10.8326V8.92721L10.6236 10.3855V18.2526C10.4095 18.3059 10.206 18.3326 9.99864 18.3326C9.79126 18.3326 9.58772 18.3059 9.37363 18.2526V10.3855L1.875 6.63623C1.94504 6.38537 2.03848 6.16074 2.16313 5.94906C2.25057 5.80057 2.35031 5.66364 2.46504 5.53371L9.99864 9.3005L12.8205 7.88958L5.26782 3.73562Z" fill="white"/>
    </svg>
    ), 
  },
  { label: 'Get a Package', 
  svg: (
    <svg 
      xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M7.01988 17.1824C8.4829 17.9501 9.21441 18.334 10.0013 18.334V10.0007L2.19965 5.89453C2.18816 5.91299 2.17688 5.93163 2.1658 5.95045C1.66797 6.79587 1.66797 7.84789 1.66797 9.95192V10.0494C1.66797 12.1534 1.66797 13.2055 2.1658 14.0509C2.66363 14.8963 3.56016 15.3668 5.35321 16.3077L7.01988 17.1824Z" fill="white"/>
      <path opacity="0.7" d="M14.649 3.69423L12.9823 2.81961C11.5193 2.05185 10.7878 1.66797 10.0009 1.66797C9.21398 1.66797 8.48247 2.05185 7.01945 2.81961L5.35278 3.69423C3.59965 4.61424 2.70356 5.08448 2.19922 5.89517L10.0009 10.0013L17.8025 5.89517C17.2982 5.08448 16.4021 4.61424 14.649 3.69423Z" fill="white"/>
      <path opacity="0.5" d="M17.8355 5.95045C17.8244 5.93163 17.8131 5.91299 17.8017 5.89453L10 10.0007V18.334C10.7869 18.334 11.5184 17.9501 12.9814 17.1824L14.6481 16.3077C16.4411 15.3668 17.3377 14.8963 17.8355 14.0509C18.3333 13.2055 18.3333 12.1534 18.3333 10.0494V9.95192C18.3333 7.84789 18.3333 6.79587 17.8355 5.95045Z" fill="white"/>
      <path d="M5.26782 3.73562C5.29518 3.72126 5.32276 3.70679 5.35055 3.6922L6.59515 3.03906L14.1791 7.21025L17.5322 5.53371C17.647 5.66364 17.7467 5.80057 17.8341 5.94906C17.9588 6.16074 18.0522 6.38537 18.1223 6.63623L14.7903 8.30221V10.8326C14.7903 11.1778 14.5105 11.4576 14.1653 11.4576C13.8201 11.4576 13.5403 11.1778 13.5403 10.8326V8.92721L10.6236 10.3855V18.2526C10.4095 18.3059 10.206 18.3326 9.99864 18.3326C9.79126 18.3326 9.58772 18.3059 9.37363 18.2526V10.3855L1.875 6.63623C1.94504 6.38537 2.03848 6.16074 2.16313 5.94906C2.25057 5.80057 2.35031 5.66364 2.46504 5.53371L9.99864 9.3005L12.8205 7.88958L5.26782 3.73562Z" fill="white"/>
    </svg>
    ), 
  },
  { label: 'Wishes',
  svg: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path opacity="0.5" d="M3.40461 9.07856C3.77037 7.12782 3.95325 6.15245 4.64756 5.57623C5.34188 5 6.33424 5 8.31897 5H11.6864C13.6711 5 14.6635 5 15.3578 5.57623C16.0521 6.15245 16.235 7.12782 16.6007 9.07856L17.2257 12.4119C17.7401 15.155 17.9972 16.5265 17.2475 17.4299C16.4977 18.3333 15.1023 18.3333 12.3114 18.3333H7.69397C4.90309 18.3333 3.50765 18.3333 2.75788 17.4299C2.00812 16.5265 2.26528 15.155 2.77961 12.4119L3.40461 9.07856Z" fill="white"/>
      <path d="M8.125 4.1665C8.125 3.13097 8.96447 2.2915 10 2.2915C11.0355 2.2915 11.875 3.13097 11.875 4.1665V4.99984C11.875 4.99983 11.875 4.99984 11.875 4.99984C12.3467 4.99999 12.7599 5.00129 13.1249 5.01224C13.125 5.00811 13.125 5.00398 13.125 4.99984V4.1665C13.125 2.44061 11.7259 1.0415 10 1.0415C8.27411 1.0415 6.875 2.44061 6.875 4.1665V4.99984C6.875 5.00399 6.87504 5.00813 6.87512 5.01226C7.24011 5.0013 7.65331 5.00001 8.125 4.99986C8.125 4.99985 8.125 4.99986 8.125 4.99986V4.1665Z" fill="white"/>
      <path d="M9.20238 13.2215C8.47071 12.6846 7.5 11.8178 7.5 10.9974C7.5 9.60308 8.87504 9.08249 10 10.1596C11.125 9.08249 12.5 9.60308 12.5 10.9974C12.5 11.8178 11.5293 12.6846 10.7976 13.2215C10.4478 13.4781 10.2729 13.6065 10 13.6065C9.72707 13.6065 9.55218 13.4781 9.20238 13.2215Z" fill="white"/>
    </svg>  
    ),
  }, 
  { label: 'Moments', 
  svg: (
    <svg 
      xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path opacity="0.5" d="M3.4007 9.91059C3.76647 7.95985 3.94935 6.98449 4.64366 6.40826C5.33797 5.83203 6.33033 5.83203 8.31506 5.83203H11.6825C13.6672 5.83203 14.6596 5.83203 15.3539 6.40826C16.0482 6.98449 16.2311 7.95985 16.5968 9.91059L17.2218 13.2439C17.7362 15.987 17.9933 17.3585 17.2436 18.262C16.4938 19.1654 15.0984 19.1654 12.3075 19.1654H7.69006C4.89918 19.1654 3.50374 19.1654 2.75398 18.262C2.00421 17.3585 2.26138 15.987 2.7757 13.2439L3.4007 9.91059Z" fill="white"/>
      <path d="M8.125 4.98827C8.125 3.95273 8.96447 3.11327 10 3.11327C11.0355 3.11327 11.875 3.95273 11.875 4.98827V5.8216C12.3467 5.82175 12.7599 5.82305 13.1249 5.834C13.125 5.82988 13.125 5.82574 13.125 5.8216V4.98827C13.125 3.26238 11.7259 1.86327 10 1.86327C8.27411 1.86327 6.875 3.26238 6.875 4.98827V5.8216C6.875 5.82575 6.87504 5.82989 6.87512 5.83403C7.24011 5.82306 7.65331 5.82177 8.125 5.82162V4.98827Z" fill="white"/>
      <path d="M8.23007 13.1239C8.48777 13.853 9.18313 14.3739 9.99854 14.3739C10.814 14.3739 11.5093 13.853 11.767 13.1239C11.882 12.7985 12.2391 12.6279 12.5646 12.7429C12.89 12.858 13.0606 13.215 12.9456 13.5405C12.5169 14.7534 11.3602 15.6239 9.99854 15.6239C8.63692 15.6239 7.48022 14.7534 7.05152 13.5405C6.93649 13.215 7.10707 12.858 7.43252 12.7429C7.75796 12.6279 8.11504 12.7985 8.23007 13.1239Z" fill="white"/>
    </svg>
    ),
  },
  { label: 'About Me', 
  svg: (
    <svg 
      xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path opacity="0.5" d="M17.1142 17.1142C18.3346 15.8939 18.3346 13.9297 18.3346 10.0013C18.3346 9.71675 18.3346 9.57448 18.322 9.42981C18.2631 8.75538 17.9896 8.09511 17.5544 7.57658C17.461 7.46536 17.3579 7.36228 17.1518 7.15612L12.8465 2.85083C12.6403 2.64468 12.5372 2.54159 12.426 2.44823C11.9075 2.01297 11.2472 1.73947 10.5728 1.6806C10.4281 1.66797 10.2859 1.66797 10.0013 1.66797C6.07293 1.66797 4.10875 1.66797 2.88836 2.88836C1.66797 4.10875 1.66797 6.07293 1.66797 10.0013C1.66797 13.9297 1.66797 15.8939 2.88836 17.1142C3.45839 17.6843 4.19069 17.9881 5.20964 18.1499L6.45964 18.2774C7.4228 18.3346 8.58426 18.3346 10.0013 18.3346C11.4183 18.3346 12.5798 18.3346 13.543 18.2774L14.793 18.1499C15.8119 17.9881 16.5442 17.6843 17.1142 17.1142Z" fill="white"/>
        <path d="M5.83203 6.04297C5.48685 6.04297 5.20703 6.32279 5.20703 6.66797C5.20703 7.01315 5.48685 7.29297 5.83203 7.29297H10.832C11.1772 7.29297 11.457 7.01315 11.457 6.66797C11.457 6.32279 11.1772 6.04297 10.832 6.04297H5.83203Z" fill="white"/>
        <path d="M10.8754 13.543C11.6241 13.5429 12.2485 13.5429 12.7441 13.6096C13.2673 13.6799 13.7396 13.8346 14.1192 14.2142C14.4987 14.5937 14.6534 15.0661 14.7238 15.5892C14.7904 16.0849 14.7904 16.7092 14.7904 17.4579V17.458L14.7904 18.1523L13.5404 18.2695V17.5013C13.5404 16.698 13.539 16.1583 13.4849 15.7558C13.4332 15.3712 13.3439 15.2067 13.2353 15.0981C13.1267 14.9895 12.9621 14.9001 12.5775 14.8484C12.175 14.7943 11.6354 14.793 10.832 14.793H9.16537C8.36202 14.793 7.82239 14.7943 7.41986 14.8484C7.03531 14.9001 6.87073 14.9895 6.76213 15.0981C6.65353 15.2067 6.56418 15.3712 6.51248 15.7558C6.45836 16.1583 6.45703 16.698 6.45703 17.5013V18.2728L5.20703 18.1491V17.458V17.4579C5.20701 16.7092 5.20699 16.0849 5.27363 15.5892C5.34396 15.0661 5.49869 14.5937 5.87825 14.2142C6.25781 13.8346 6.73014 13.6799 7.2533 13.6096C7.74895 13.5429 8.37329 13.5429 9.12201 13.543H9.12203H10.8754H10.8754Z" fill="white"/>
    </svg>
    ),
  },
];




  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage: 'url("/gradientBG2.svg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        maxWidth: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
      }}
    >
    {/* -------------------------------------------------------------------------------opcatity added-------------------------------- */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.12)', // Adjust opacity here (0.3 = 30%)
            // Or use teal overlay: backgroundColor: 'rgba(6, 59, 79, 0.4)',
            zIndex: 0,
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
    {/* -------------------------------------------------------------------------------opcatity added-------------------------------- */}


      {/* ---------- Header ---------- */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 20px',
          position: 'relative',
          flexWrap: 'nowrap',
          // overflow: 'hidden',
          zIndex: 999,
              }}
      >
        {/* Left Logo */}
        {/* <div
          className="logo-container"
          onClick={() => handleMenuClick("Ask Mindy")} // Add this onClick handler
          style={{
            flexShrink: 0,
            minWidth: 'fit-content',
             cursor: 'pointer', // Add cursor pointer to indicate it's clickable
          }}
        >
          <img
            src="/mindylogo55.svg"
            alt="MindyKnows Logo"
            style={{
              height: '70px',
              width: 'auto',
              filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.5))',
            }}
          />
        </div> */}


        {/* Left Logo */}
          <div
            className="logo-container"
            onClick={() => {
              setShowServiceSelector(true); // Show the ServiceSelector landing page
              setActiveMenu("Ask Mindy"); // Reset active menu to Ask Mindy
              setServicesOpen(false); // Close any open service sidebars
              setWishlistOpen(false);
              setMemoryOpen(false);
            }}
            style={{
              flexShrink: 0,
              minWidth: 'fit-content',
              cursor: 'pointer',
            }}
          >
            <img
              src="/mindylogo55.svg"
              alt="MindyKnows Logo"
              style={{
                height: '70px',
                width: 'auto',
                filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.5))',
              }}
            />
          </div>

        {/* Center Menu */}
        <div
          className="menu-container"
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '4px',
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.10)',
            backdropFilter: 'blur(20px)',
            width: 'fit-content',
            maxWidth: '80%',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            zIndex: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'nowrap',
            }}
          >
            {menuItems.map((item) => (
              <div
                key={item.label}
                onClick={() => handleMenuClick(item.label)}
                style={{
                  display: 'flex',
                  padding: '4px 8px',
                  alignItems: 'center',
                  gap: '6px',
                  borderRadius: '100px',
                  cursor: 'pointer',
                  background: activeMenu === item.label ? '#9AAC89' : 'transparent',
                  color: '#FFF',
                  fontFamily: 'Manrope',
                  fontSize: '15px',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.svg}
                <span className="menu-text">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Avatar + Dropdown */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            alignSelf: 'stretch',
            flexShrink: 0,
          }}
        >
          {/* Hamburger Icon (Mobile only) */}
          <div
            className="hamburger-icon"
            onClick={() => setMobileMenuOpen(true)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.15)',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: '22px',
                height: '2px',
                background: '#fff',
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  width: '22px',
                  height: '2px',
                  background: '#fff',
                  top: '-6px',
                  left: '0',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  width: '22px',
                  height: '2px',
                  background: '#fff',
                  top: '6px',
                  left: '0',
                }}
              />
            </div>
          </div>

          {/* Right Side Slide Menu (Mobile) */}
          {mobileMenuOpen && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                height: '100vh',
                width: '70%',
                backgroundColor: '#063b4f',
                zIndex: 105,
                display: 'flex',
                flexDirection: 'column',
                padding: '20px',
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <button
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  alignSelf: 'flex-end',
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '28px',
                  cursor: 'pointer',
                  marginBottom: '20px',
                }}
              >
                ✕
              </button>

              {menuItems.map((item) => (
                <div
                  key={item.label}
                  onClick={() => {
                    handleMenuClick(item.label);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 0',
                    color: '#fff',
                    fontSize: '18px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                  }}
                >
                  {item.svg}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Responsive Styling */}
          <style>
            {`
              /* Hide center menu on smaller screens */
              @media (max-width: 1076px) {
                .menu-container {
                  display: none !important;
                }
                .hamburger-icon {
                  display: flex !important;
                }
                .logo-container img {
                  height: 40px !important;
                }
                header {
                  padding: 8px 14px !important;
                }
              }

              /* Gradual width scaling for center menu */
              @media (max-width: 1400px) {
                .menu-container {
                  max-width: 70% !important;
                }
              }
              // @media (max-width: 1200px) {
              //   .menu-container {
              //     max-width: 60% !important;
              //   }
              // }

              /* Hide text labels, show only icons for mid-small screens */
              @media (max-width: 1150px) and (min-width: 1077px) {
                .menu-text {
                  display: none !important;
                }
              }
            `}
          </style>

          {/* Avatar & Dropdown */}
          <div
            style={{
              display: "flex",
              padding: "4px",
              alignItems: "center",
              gap: "4px",
              borderRadius: "30px",
              background: "rgba(255, 255, 255, 0.20)",
              backdropFilter: "blur(30px)",
              position: "relative",
              cursor: "pointer",
            }}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#E0E0E0',
                backgroundImage: isParent
                  ? 'url("/parent.png")'
                  : 'url("/student.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexShrink: 0,
              }}
            />
            {dropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "50px",
                  right: "0",
                  background: "white",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  padding: "8px 12px",
                  zIndex: 10,
                  minWidth: "120px",
                }}
              >
                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    padding: "6px 12px",
                    background: "#FF4D4F",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>



      {/* ---------- Main Content Section ---------- */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          padding: !mobileWidth ? "0 20px" : '0 10px',
          width: "100vw",
          height: "100vh",
          boxSizing: "border-box",
          overflowY: "hidden",
          backgroundSize: "cover",
          backgroundPosition: "center",
          gap: "75px", //  Controls distance between left and right sections
        }}
      >
        {/* Left Section */}

        <div
          style={{
            display: !mobileWidth ? "flex" : "none",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "32px",
            width: "100%",
            maxWidth: "250px",
          }}
        >
          <style>
            {`
              @media (max-width: 800px) {
                .hide-on-1048 {
                  display: none !important;
                }
              }
            `}
          </style>

          <div
            className="hide-on-1048"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "0px",
            }}
          >
            <p
              style={{
                fontFamily: "Manrope, sans-serif",
                fontSize: "26px",
                fontWeight: 400, // or 300 for thinner
                color: "#fff",
                margin: 0,

                whiteSpace: "nowrap",
              }}
            >
              What's up, {userName}?
            </p>

            

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                marginTop: "16px",
                position: "relative",
              }}
            >
              <img
                src="/mindyVM3.png"
                alt="Mindy"
                style={{
                  width: "700px",
                  height: "450px",
                  objectFit: "contain",
                  marginLeft: "-230px",

                  /* 👇 Vibrancy fix */
                  filter: "brightness(1.06) contrast(1.06) saturate(1.2)",
                  imageRendering: "auto",
                }}
              />


              {/* Voice Icon Overlay on Mindy */}
              <div
                style={{
                  display: "flex",
                  height: "0px",
                  padding: "8px 16px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                  position: "absolute",
                  left: "48px",
                  top: "114px",
                  borderRadius: "100px",
                  cursor: "pointer",
                }}
                onClick={() => setShowFullscreenVideo(true)}

              >
                <img
                  src="/voice.png"
                  alt="Voice Icon"
                  style={{ width: "90px", height: "90px" }}
                />
              </div>

              {/*----------------------MindyAi Voice Modal-------------------*/}

              {aiModal && (
                <div
                  style={{
                    display: "flex",
                    width: "100vw",
                    height: "100vh",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    borderRadius: "20px",
                    background: "rgba(0, 0, 0, 0.20)",
                    boxShadow: "0 10px 25px 0 rgba(0, 10, 25, 0.08)",
                    zIndex: 999,
                    backdropFilter: "blur(20px)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "8px",
                    }}
                  >
                    <div
                      onClick={() => setShowFullscreenVideo(false)}

                      style={{ cursor: "pointer" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M15.7128 16.773L7.22748 8.28777C6.93757 7.99785 6.93757 7.51702 7.22748 7.22711C7.5174 6.93719 7.99823 6.93719 8.28814 7.22711L16.7734 15.7124C17.0633 16.0023 17.0633 16.4831 16.7734 16.773C16.4835 17.063 16.0027 17.063 15.7128 16.773Z"
                          fill="white"
                        />
                        <path
                          d="M7.22658 16.773C6.93666 16.4831 6.93666 16.0023 7.22658 15.7124L15.7119 7.22711C16.0018 6.93719 16.4826 6.93719 16.7725 7.22711C17.0624 7.51702 17.0624 7.99785 16.7725 8.28777L8.28724 16.773C7.99732 17.063 7.51649 17.063 7.22658 16.773Z"
                          fill="white"
                        />
                      </svg>
                    </div>

                    <div
                      style={{
                        position: "relative",
                        width: "400px",
                        height: "550px",
                        borderRadius: "20px",
                        overflow: "hidden",
                        background: "rgba(0,0,0,0.35)",
                        boxShadow: "0px 20px 60px rgba(0,0,0,0.45)",
                      }}
                    >
                      {/* Mute / Unmute Button */}
                      <button
                        onClick={toggleMute}
                        style={{
                          position: "absolute",
                          top: "14px",
                          right: "14px",
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: "rgba(0,0,0,0.45)",
                          border: "none",
                          cursor: "pointer",
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isMuted ? "🔇" : "🔊"}
                      </button>

                      {/* Video */}
                      <video
                        ref={videoRef}
                        playsInline
                        controls={false}
                        disablePictureInPicture
                        disableRemotePlayback
                        style={{
                          width: "100vw",
                          height: "100vh",
                          objectFit: "contain",
                          background: "black",
                          pointerEvents: "none",
                        }}
                      >

                        <source
                          src="https://imvi-app-videos.s3.us-west-2.amazonaws.com/videos/landing_page_video.mp4"
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                    </div>

                  </div>
                </div>
              )}
            </div>

            {/* ------------------------------------------adding the new notification card------------------- */}
            <div
              className="notification-container"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px", // spacing between notifications
              }}
            >
              {/* First Notification */}
              <div
                className="notification-card"
                style={{
                  borderRadius: "32px",
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(30px)",
                  display: "flex",
                  width: "262px",
                  padding: "12px",
                  gap: "12px",
                  position: "relative",
                  zIndex: 99,
                  marginTop: "-240px",
                  fontFamily: "Manrope",
                  fontSize: "15px",
                  color: "white",
                }}
              >
                <div className="icon sparkle" style={{ fontSize: "15px" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M7.45284 2.71266C7.8276 1.76244 9.1724 1.76245 9.54716 2.71267L10.7085 5.65732C10.8229 5.94743 11.0526 6.17707 11.3427 6.29148L14.2873 7.45284C15.2376 7.8276 15.2376 9.1724 14.2873 9.54716L11.3427 10.7085C11.0526 10.8229 10.8229 11.0526 10.7085 11.3427L9.54716 14.2873C9.1724 15.2376 7.8276 15.2376 7.45284 14.2873L6.29148 11.3427C6.17707 11.0526 5.94743 10.8229 5.65732 10.7085L2.71266 9.54716C1.76244 9.1724 1.76245 7.8276 2.71267 7.45284L5.65732 6.29148C5.94743 6.17707 6.17707 5.94743 6.29148 5.65732L7.45284 2.71266Z"
                      fill="white"
                    />
                    <path
                      opacity="0.5"
                      d="M16.9245 13.3916C17.1305 12.8695 17.8695 12.8695 18.0755 13.3916L18.9761 15.6753C19.039 15.8348 19.1652 15.961 19.3247 16.0239L21.6084 16.9245C22.1305 17.1305 22.1305 17.8695 21.6084 18.0755L19.3247 18.9761C19.1652 19.039 19.039 19.1652 18.9761 19.3247L18.0755 21.6084C17.8695 22.1305 17.1305 22.1305 16.9245 21.6084L16.0239 19.3247C15.961 19.1652 15.8348 19.039 15.6753 18.9761L13.3916 18.0755C12.8695 17.8695 12.8695 17.1305 13.3916 16.9245L15.6753 16.0239C15.8348 15.961 15.961 15.8348 16.0239 15.6753L16.9245 13.3916Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div
                  className="text"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <h4 style={{ margin: 0 }}>Upcoming Campus Event</h4>
                  <p style={{ margin: 0, opacity: 0.8 }}>
                    Homecoming Weekend is in 15 days  
                  </p>
                </div>

                {/* Close button */}
                <button
                  onClick={(e) =>
                    (e.currentTarget.parentElement.style.display = "none")
                  }
                  style={{
                    position: "absolute",
                    top: "-10px", // move it above the card
                    right: "4px", // move slightly outside
                    width: "20px",
                    height: "22px",
                    background: "rgba(255,255,255,0.1)", // transparent glass circle
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M15.7128 16.773L7.22748 8.28777C6.93757 7.99785 6.93757 7.51702 7.22748 7.22711C7.5174 6.93719 7.99823 6.93719 8.28814 7.22711L16.7734 15.7124C17.0633 16.0023 17.0633 16.4831 16.7734 16.773C16.4835 17.063 16.0027 17.063 15.7128 16.773Z"
                      fill="white"
                    />
                    <path
                      d="M7.22658 16.773C6.93666 16.4831 6.93666 16.0023 7.22658 15.7124L15.7119 7.22711C16.0018 6.93719 16.4826 6.93719 16.7725 7.22711C17.0624 7.51702 17.0624 7.99785 16.7725 8.28777L8.28724 16.773C7.99732 17.063 7.51649 17.063 7.22658 16.773Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>

              <div
                className="notification-card"
                style={{
                  borderRadius: "32px",
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(30px)",
                  display: "flex",
                  width: "262px",
                  padding: "12px",
                  gap: "12px",
                  position: "relative",
                  zIndex: 99,
                  fontFamily: "Manrope",
                  fontSize: "15px",
                  color: "white",
                }}
              >
                <div className="icon party" style={{ fontSize: "15px" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M10.925 2.36021C11.2784 2.57626 11.3897 3.03789 11.1737 3.3913C11.0169 3.64775 11.0562 3.97821 11.2687 4.19075L11.3666 4.28863C11.9553 4.87729 12.1723 5.74206 11.9315 6.53894C11.8116 6.93543 11.393 7.1597 10.9965 7.03985C10.6 6.91999 10.3758 6.50141 10.4956 6.10492C10.5765 5.83734 10.5036 5.54695 10.306 5.34929L10.2081 5.25141C9.50556 4.54889 9.37568 3.45659 9.89389 2.60892C10.1099 2.25551 10.5716 2.14416 10.925 2.36021Z"
                      fill="white"
                    />
                    <path
                      d="M19.9733 7.04681C19.8349 7.10014 19.7122 7.22283 19.4668 7.46822C19.2214 7.71362 19.0987 7.83631 19.0454 7.97472C18.9829 8.1367 18.9829 8.31609 19.0454 8.47807C19.0987 8.61649 19.2214 8.73918 19.4668 8.98457C19.7122 9.22996 19.8349 9.35266 19.9733 9.40599C20.1353 9.4684 20.3146 9.4684 20.4766 9.40599C20.615 9.35266 20.7377 9.22996 20.9831 8.98457C21.2285 8.73918 21.3512 8.61649 21.4045 8.47807C21.467 8.31609 21.467 8.1367 21.4045 7.97472C21.3512 7.83631 21.2285 7.71362 20.9831 7.46822C20.7377 7.22283 20.615 7.10014 20.4766 7.04681C20.3146 6.9844 20.1353 6.9844 19.9733 7.04681Z"
                      fill="white"
                    />
                    <path
                      d="M21.4067 12.5596C21.045 12.4015 20.6244 12.4679 20.329 12.7298C19.5197 13.4474 18.3421 13.5667 17.4054 13.0259L17.1925 12.903C16.8338 12.6959 16.7109 12.2372 16.918 11.8785C17.1251 11.5198 17.5838 11.3968 17.9425 11.604L18.1554 11.7268C18.5329 11.9448 19.0076 11.8967 19.3338 11.6075C20.0666 10.9577 21.1102 10.7929 22.0077 11.1852L22.2991 11.3127C22.6787 11.4786 22.8518 11.9208 22.6859 12.3003C22.5199 12.6799 22.0777 12.853 21.6982 12.6871L21.4067 12.5596Z"
                      fill="white"
                    />
                    <path
                      opacity="0.7"
                      d="M13.5649 4.39648C13.766 4.19542 13.8665 4.09489 13.9828 4.05804C14.0811 4.02688 14.1866 4.02688 14.2849 4.05804C14.4012 4.09489 14.5017 4.19542 14.7028 4.39648C14.9038 4.59753 15.0043 4.69806 15.0412 4.8143C15.0724 4.91262 15.0724 5.01817 15.0412 5.11648C15.0043 5.23272 14.9038 5.33325 14.7028 5.5343C14.5017 5.73536 14.4012 5.83589 14.2849 5.87274C14.1866 5.90391 14.0811 5.90391 13.9828 5.87274C13.8665 5.83589 13.766 5.73536 13.5649 5.53431C13.3639 5.33325 13.2633 5.23272 13.2265 5.11648C13.1953 5.01817 13.1953 4.91262 13.2265 4.8143C13.2633 4.69806 13.3639 4.59753 13.5649 4.39648Z"
                      fill="white"
                    />
                    <path
                      opacity="0.7"
                      d="M19.0555 15.3134C19.265 15.1039 19.6046 15.1039 19.8141 15.3134C20.0236 15.5228 20.0236 15.8624 19.8141 16.0719C19.6046 16.2814 19.265 16.2814 19.0555 16.0719C18.8461 15.8624 18.8461 15.5228 19.0555 15.3134Z"
                      fill="white"
                    />
                    <g opacity="0.5">
                      <path
                        d="M6.92273 3.94079C7.1322 3.73132 7.47181 3.73132 7.68128 3.94079C7.89075 4.15026 7.89075 4.48988 7.68128 4.69935C7.47181 4.90882 7.1322 4.90882 6.92273 4.69935C6.71326 4.48988 6.71326 4.15026 6.92273 3.94079Z"
                        fill="white"
                      />
                      <path
                        d="M17.6848 4.72163C18.091 4.80287 18.3544 5.19799 18.2732 5.60416L18.1292 6.32409C17.931 7.31491 17.2171 8.12357 16.2585 8.4431C15.8106 8.59241 15.477 8.97028 15.3844 9.43326L15.2405 10.1532C15.1592 10.5594 14.7641 10.8228 14.3579 10.7415C13.9518 10.6603 13.6884 10.2652 13.7696 9.85903L13.9136 9.13909C14.1117 8.14828 14.8256 7.33961 15.7842 7.02008C16.2321 6.87077 16.5657 6.4929 16.6583 6.02992L16.8023 5.30998C16.8835 4.90381 17.2786 4.6404 17.6848 4.72163Z"
                        fill="white"
                      />
                    </g>
                    <path
                      opacity="0.2"
                      d="M17.5009 9.74157C17.7103 9.53211 18.0499 9.53211 18.2594 9.74157C18.4689 9.95104 18.4689 10.2907 18.2594 10.5001C18.0499 10.7096 17.7103 10.7096 17.5009 10.5001C17.2914 10.2907 17.2914 9.95104 17.5009 9.74157Z"
                      fill="white"
                    />
                    <path
                      opacity="0.5"
                      d="M4.01305 15.7618L5.70254 10.6933C6.46855 8.39525 6.85156 7.24623 7.75782 7.03229C8.66408 6.81835 9.52051 7.67478 11.2334 9.38764L14.6124 12.7666C16.3252 14.4795 17.1817 15.3359 16.9677 16.2422C16.7538 17.1484 15.6048 17.5314 13.3067 18.2975L8.23822 19.987L8.23821 19.987C5.4728 20.9088 4.0901 21.3697 3.36022 20.6398C2.63034 19.9099 3.09124 18.5272 4.01305 15.7618Z"
                      fill="white"
                    />
                    <path
                      d="M8.8001 7.50424L8.85072 7.25922C8.45761 7.02857 8.111 6.94867 7.75679 7.03229C7.61235 7.06638 7.4812 7.12423 7.35967 7.2067L8.05611 7.35058C7.57757 7.25172 7.41498 7.21808 7.35967 7.2067C7.34804 7.2146 7.3364 7.22279 7.32494 7.23114L7.31964 7.25736C7.31242 7.29328 7.30199 7.34575 7.28885 7.41325C7.26258 7.54824 7.22547 7.74345 7.18165 7.98662C7.09406 8.47264 6.97937 9.15184 6.87078 9.92573C6.65564 11.4589 6.45638 13.4179 6.55904 14.9834C6.62115 15.9306 6.81822 17.1057 6.9941 18.0238C7.08286 18.4872 7.16784 18.8933 7.23066 19.1838C7.26209 19.3291 7.28803 19.4457 7.30619 19.5264L7.32733 19.6195L7.333 19.6441L7.33493 19.6525C7.33494 19.6526 7.33512 19.6533 8.0656 19.4833L7.33493 19.6525L7.47191 20.2412C7.71447 20.1612 7.96933 20.0762 8.23717 19.987L8.90132 19.7656L8.79453 19.3066L8.78944 19.2845L8.76953 19.1968C8.75221 19.1199 8.7272 19.0075 8.69676 18.8667C8.63584 18.585 8.55336 18.1909 8.46732 17.7416C8.29357 16.8346 8.11155 15.7351 8.05582 14.8852C7.96377 13.4814 8.1436 11.6495 8.35623 10.1342C8.46152 9.38377 8.57288 8.72427 8.65787 8.25265C8.70034 8.017 8.73615 7.82867 8.76123 7.69981C8.77376 7.63539 8.78361 7.58587 8.79026 7.55277L8.79777 7.51563L8.7996 7.50665L8.8001 7.50424Z"
                      fill="white"
                    />
                    <path
                      d="M13.0393 18.3863L11.6162 18.8606L11.5238 18.5826L12.2356 18.3461C11.5238 18.5826 11.5239 18.5827 11.5238 18.5826L11.5229 18.5798L11.5209 18.5738L11.5138 18.552C11.5077 18.5333 11.499 18.5064 11.4881 18.472C11.4663 18.4031 11.4354 18.3042 11.3986 18.1811C11.325 17.9354 11.227 17.5917 11.1288 17.1985C10.9367 16.4293 10.7274 15.407 10.7274 14.5552C10.7274 13.7034 10.9367 12.6811 11.1288 11.9119C11.227 11.5187 11.325 11.175 11.3986 10.9293C11.4354 10.8062 11.4663 10.7073 11.4881 10.6384C11.499 10.604 11.5077 10.5771 11.5138 10.5584L11.5209 10.5366L11.5229 10.5305L11.5235 10.5287C11.5235 10.5287 11.5238 10.5278 12.2356 10.7643L11.5238 10.5278L11.7355 9.89084L12.9224 11.0777C12.921 11.0824 12.9194 11.0872 12.9178 11.0922C12.8982 11.154 12.8698 11.245 12.8356 11.3594C12.767 11.5886 12.6755 11.9095 12.5841 12.2753C12.3971 13.0241 12.2274 13.8973 12.2274 14.5552C12.2274 15.2131 12.3971 16.0863 12.5841 16.8351C12.6755 17.2009 12.767 17.5218 12.8356 17.751C12.8698 17.8654 12.8982 17.9564 12.9178 18.0182C12.9276 18.0491 12.9352 18.0727 12.9403 18.0881L12.9458 18.1052L12.9471 18.109L13.0393 18.3863Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div
                  className="text"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <h4 style={{ margin: 0 }}>
                    {/* {cognitoName ? `${cognitoName}'s Birthday in 5 days` : 'Birthday in 5 days'} */}
                    {"Maggie's Acapella Concert is in 5 days"}
                  </h4>
                  <p style={{ margin: 0, opacity: 0.8 }}>
                    {/* {cognitoName ? `Send birthday gift to ${cognitoName}` : 'Send birthday gift'} */}
                    {"Remember to send flowers"}
                  </p>
                </div>

                {/* Close button */}
                <button
                  onClick={(e) =>
                    (e.currentTarget.parentElement.style.display = "none")
                  }
                  style={{
                    position: "absolute",
                    top: "-10px", // move it above the card
                    right: "4px", // move slightly outside
                    width: "20px",
                    height: "22px",
                    background: "rgba(255,255,255,0.1)", // transparent glass circle
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M15.7128 16.773L7.22748 8.28777C6.93757 7.99785 6.93757 7.51702 7.22748 7.22711C7.5174 6.93719 7.99823 6.93719 8.28814 7.22711L16.7734 15.7124C17.0633 16.0023 17.0633 16.4831 16.7734 16.773C16.4835 17.063 16.0027 17.063 15.7128 16.773Z"
                      fill="white"
                    />
                    <path
                      d="M7.22658 16.773C6.93666 16.4831 6.93666 16.0023 7.22658 15.7124L15.7119 7.22711C16.0018 6.93719 16.4826 6.93719 16.7725 7.22711C17.0624 7.51702 17.0624 7.99785 16.7725 8.28777L8.28724 16.773C7.99732 17.063 7.51649 17.063 7.22658 16.773Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </div>

              <div
                className="notification-card"
                style={{
                  borderRadius: "32px",
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(30px)",
                  display: "flex",
                  // alignItems: 'center', // align icon and text vertically
                  width: "262px",
                  padding: "12px",
                  gap: "12px",
                  position: "relative",
                  zIndex: 99,
                  // marginTop:'-120px',
                  fontFamily: "Manrope",
                  fontSize: "15px",
                  color: "white",
                }}
              >
                <div className="icon party" style={{ fontSize: "15px" }}>
                  🔐
                </div>
                <div
                  className="text"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <h4 style={{ margin: 0 }}>Unlock mindyVIP</h4>
                  <p style={{ margin: 0, opacity: 0.8 }}>
                    Get 24/7 access to a real-life Mindy (RLM), special <br/>
                    exclusive discounts, and higher level of support.
                  </p>
                </div>
              </div>
            </div>

            {/* ------------------------------------------adding the new notification card------------------- */}

            {/* <div style={{ display: 'flex', justifyContent: 'center', width: '100%',  zIndex: 9999,  }}>
              <img
                src="/pronotf.png"
                alt="Pronotf"
                style={{ width: '200px', height: 'auto', objectFit: 'contain' }}
              />
            </div> */}
          </div>
        </div>

        {/* ---------- Right Section (Dynamic Content) - Moved much closer ---------- */}
        <div style={{display: 'flex', alignItems: 'flex-start', width: '100%', padding: "10px 0", overflow: "hidden" }}>
          {" "}
          {/*marginRight: '425px'*/}
          {/* Render Chat closer to left panel */}
          {activeMenu === "Ask Mindy" && (
            <div
              style={{
                display: "flex",
                justifyContent: "center", // center horizontally in remaining space

                // width: !mobileWidth ? "calc(100% - 500px)" : "", // take remaining width
                height: "calc(100vh - 100px)",
                overflowY: "scroll",
                paddingBottom: "100px",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
              className="hide-scrollbar"
            >
              <div style={{ maxWidth: "680px", width: "100%" }}>
                <Chat key="ask-mindy" />
              </div>
            </div>
          )}
          {activeMenu === "Crisis Support" && (
            <div
              style={{
                display: "flex",
                justifyContent: "center", // center horizontally in remaining space

                width: "calc(100% - 500px)", // take remaining width
                height: "calc(100vh - 100px)",
                overflowY: "scroll",
                paddingBottom: "100px",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
              className="hide-scrollbar"
            >
              <div style={{ maxWidth: "680px", width: "100%" }}>
                {/* <CrisisChat key="crisis-support" /> */}
                <CrisisChat key="crisis-support" />
              </div>
            </div>
          )}
          {activeMenu === "Get a Hand" && (
            <div style={{ display: "flex", gap: "16px" }}>
              <ServicesSidebar
                isOpen={servicesOpen}
                onClose={() => setServicesOpen(false)}
              />
              {!mobileWidth && <div
                style={{
                  maxWidth: "660px",
                  height: "calc(100vh - 100px)",
                  overflowY: "scroll",
                  paddingBottom: "100px",
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                className="hide-scrollbar"
              >
                <Chat key="get-a-hand" /> {/* Always show Ask Mindy AI here */}
              </div>}
            </div>
          )}

          {activeMenu === 'Get a Package' && (
            <div style={{ display: 'flex', gap: '16px' }}>
              <Packages
                isOpen={servicesOpen}
                onClose={() => setServicesOpen(false)}
              />
              <div
                style={{
                  maxWidth: "660px",
                  height: "calc(100vh - 100px)",
                  overflowY: "scroll",
                  paddingBottom: "100px",
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                className="hide-scrollbar"
              >
                <Chat key="get-a-delivery" />{" "}
                {/* Always show Ask Mindy AI here */}
              </div>
            </div>
          )}
          {activeMenu === "Wishes" && (
            <div ref={wishlistRef} style={{ width: '100%' }}>
              <Wishlist
                isOpen={wishlistOpen}
                onClose={() => {
                  setWishlistOpen(false);
                  setActiveMenu("Ask Mindy");
                }}
                userName={userName}
              />
            </div>
          )}
              {/* <div
                style={{
                  maxWidth: '680px',
                  height: 'calc(100vh - 100px)',
                  overflowY: 'scroll',
                  paddingBottom: '100px',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
                className="hide-scrollbar"
              >
                <Chat /> {/* Always show Ask Mindy AI here }
              </div> */}
          {/* Moments Page */}
          {activeMenu === "Moments" && (
            <div ref={momentsRef} style={{ width: '100%' }}>
              <Moments />
            </div>
          )}
          
          {/* About Me Page */}
          {activeMenu === "About Me" && (
            <div ref={aboutMeRef} style={{ width: '100%' }}>
              <MemoryPage />
            </div>
          )}
        </div>
      </div>
      {showServiceSelector && (
        <ServiceSelector
          onSkip={() => setShowServiceSelector(false)}
          onServiceSelect={(index) => {
            setShowServiceSelector(false);
            if (index === 0) {
              setActiveMenu("Ask Mindy");
              console.log(activeMenu);
            } else if (index === 1) {
              // For immediate help, perhaps show an alert or something
              // setActiveMenu("Crisis Support");
              // handleMenuClick("Crisis Support");

              setActiveMenu('Wishes');
              handleMenuClick('Wishes');
            } else if (index === 2) {
              setActiveMenu('Get a Package');
              handleMenuClick('Get a Package');
            }
          }}
          onGetHelpNow={handleGetHelpNow}
        />
      )}
     </div> {/*opacity edded here  */}
        {showFullscreenVideo && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "black",
                  zIndex: 99999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Close (X) */}
                <button
                  onClick={() => {
                    videoRef.current?.pause();
                    setShowFullscreenVideo(false);
                  }}
                  aria-label="Close video"
                  style={{
                    position: "absolute",
                    top: "16px",
                    right: "16px",
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    zIndex: 100000,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M15.7128 16.773L7.22748 8.28777C6.93757 7.99785 6.93757 7.51702 7.22748 7.22711C7.5174 6.93719 7.99823 6.93719 8.28814 7.22711L16.7734 15.7124C17.0633 16.0023 17.0633 16.4831 16.7734 16.773C16.4835 17.063 16.0027 17.063 15.7128 16.773Z"
                      fill="white"
                    />
                    <path
                      d="M7.22658 16.773C6.93666 16.4831 6.93666 16.0023 7.22658 15.7124L15.7119 7.22711C16.0018 6.93719 16.4826 6.93719 16.7725 7.22711C17.0624 7.51702 17.0624 7.99785 16.7725 8.28777L8.28724 16.773C7.99732 17.063 7.51649 17.063 7.22658 16.773Z"
                      fill="white"
                    />
                  </svg>
                </button>
          {/* Video */}
          <video
            ref={videoRef}
            autoPlay
            loop
            playsInline
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            style={{
              width: "100vw",
              height: "100vh",
              objectFit: "contain", // or "contain"
            }}
          >
            <source
              src="https://mindy-video.s3.us-east-1.amazonaws.com/MindyKnowsVideo.mp4"
              type="video/mp4"
            />
          </video>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
